import { useMemo, useState } from "react";
import {
    Box, Button, Card, CardContent, Chip, CircularProgress, Alert,
    FormControl, InputAdornment, InputLabel,
    MenuItem, Select, Stack, Table, TableBody, TableCell,
    TableContainer, TableHead, TablePagination, TableRow, TextField,
    Tooltip, Typography,
} from "@mui/material";
import {
    CheckCircle, FileCopy, DriveFileMove, Delete, Edit,
    FolderSpecial, Search, FilterList, Close,
} from "@mui/icons-material";
import { useHistory, useHistoryStats } from "../hooks/useHistory";
import { HistoryRecordDto } from "../api/generated/models";
import { useTranslation } from "react-i18next";

const getActionIcon = (action: string) => {
    switch (action.toLowerCase()) {
        case "move":   return <DriveFileMove fontSize="small" />;
        case "copy":   return <FileCopy fontSize="small" />;
        case "delete": return <Delete fontSize="small" />;
        case "rename": return <Edit fontSize="small" />;
        default:       return <DriveFileMove fontSize="small" />;
    }
};

const ACTION_CHIP_COLORS: Record<string, { bg: string; color: string }> = {
    move:   { bg: 'rgba(37,99,235,0.1)',  color: '#2563EB' },
    copy:   { bg: 'rgba(14,165,233,0.1)', color: '#0284C7' },
    delete: { bg: 'rgba(239,68,68,0.1)',  color: '#DC2626' },
    rename: { bg: 'rgba(245,158,11,0.1)', color: '#D97706' },
};

const chipSx = { height: 22, borderRadius: 1, fontWeight: 600, fontSize: '0.7rem', border: 'none' };

const HEADER_CELL_SX = {
    fontWeight: 700, color: '#64748B', fontSize: '0.72rem',
    textTransform: 'uppercase' as const, letterSpacing: '0.05em',
    bgcolor: '#F8FAFC', borderBottom: '1px solid #E2E8F0',
};

const ACTION_TYPES = ["Move", "Copy", "Delete", "Rename"];

export default function HistoryPage() {
    const { t, i18n } = useTranslation();
    const [selectedAction, setSelectedAction] = useState("");
    const [fileSearch, setFileSearch] = useState("");
    const [selectedRule, setSelectedRule] = useState("");
    const [sinceDate, setSinceDate] = useState("");
    const [count, setCount] = useState(100);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);

    const locale = i18n.language === 'en' ? 'en-US' : 'es-ES';

    const formatDate = (dateString?: Date | string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return isNaN(date.getTime()) ? "" : date.toLocaleString(locale, {
            year: 'numeric', month: '2-digit', day: '2-digit',
            hour: '2-digit', minute: '2-digit',
        });
    };

    const { data: stats } = useHistoryStats();
    const { data: history, isLoading, error } = useHistory({
        count,
        actionType: selectedAction || undefined,
        since: sinceDate ? new Date(sinceDate) : undefined,
    });

    const ruleOptions = useMemo(() => {
        if (!history) return [];
        const names = history.map(r => r.ruleName).filter((n): n is string => !!n);
        return Array.from(new Set(names)).sort();
    }, [history]);

    const filtered = useMemo(() => {
        if (!history) return [];
        return history.filter(item => {
            if (fileSearch && !item.fileName?.toLowerCase().includes(fileSearch.toLowerCase())) return false;
            if (selectedRule && item.ruleName !== selectedRule) return false;
            return true;
        });
    }, [history, fileSearch, selectedRule]);

    const activeFilterCount = [selectedAction, fileSearch, selectedRule, sinceDate].filter(Boolean).length;

    const resetFilters = () => {
        setSelectedAction("");
        setFileSearch("");
        setSelectedRule("");
        setSinceDate("");
        setPage(0);
    };

    const onFilterChange = (setter: (v: string) => void) => (v: string) => {
        setter(v);
        setPage(0);
    };

    return (
        <Box sx={{ height: '100%', bgcolor: '#F8FAFC', display: 'flex', flexDirection: 'column', p: 2, gap: 1.5, boxSizing: 'border-box', overflow: 'hidden' }}>
            {/* Stats */}
            {stats && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2, flexShrink: 0 }}>
                    {[
                        { label: t('history.stats.totalRecords'), value: stats.total ?? 0, color: '#0F172A' },
                        { label: t('history.stats.actionTypes'), value: stats.byAction?.length ?? 0, color: '#2563EB' },
                        { label: t('history.stats.activeRules'), value: stats.byRule?.length ?? 0, color: '#10B981' },
                    ].map(({ label, value, color }) => (
                        <Card key={label} sx={{ border: '1px solid #E2E8F0', boxShadow: 'none' }}>
                            <CardContent sx={{ p: 2.5 }}>
                                <Typography variant="body2" sx={{ color: '#64748B', mb: 1 }}>{label}</Typography>
                                <Typography variant="h4" fontWeight={700} sx={{ color }}>{value}</Typography>
                            </CardContent>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Filtros */}
            <Card sx={{ border: '1px solid #E2E8F0', boxShadow: 'none', flexShrink: 0 }}>
                <CardContent sx={{ p: 2.5 }}>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                            <FilterList sx={{ fontSize: 18, color: '#64748B' }} />
                            <Typography variant="body2" fontWeight={600} sx={{ color: '#0F172A' }}>{t('history.filters')}</Typography>
                            {activeFilterCount > 0 && (
                                <Chip
                                    label={t('history.activeFilters_other', { count: activeFilterCount })}
                                    size="small"
                                    sx={{ ...chipSx, bgcolor: 'rgba(37,99,235,0.1)', color: '#2563EB' }}
                                />
                            )}
                        </Stack>
                        {activeFilterCount > 0 && (
                            <Button
                                size="small"
                                startIcon={<Close sx={{ fontSize: 14 }} />}
                                onClick={resetFilters}
                                sx={{ color: '#64748B', fontSize: '0.8rem', textTransform: 'none' }}
                            >
                                {t('history.clear')}
                            </Button>
                        )}
                    </Stack>

                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(5, 1fr)' }, gap: 2 }}>
                        <TextField
                            label={t('history.searchFile')}
                            value={fileSearch}
                            onChange={(e) => onFilterChange(setFileSearch)(e.target.value)}
                            size="small"
                            placeholder={t('history.searchFilePlaceholder')}
                            slotProps={{
                                input: {
                                    startAdornment: <InputAdornment position="start"><Search sx={{ fontSize: 16, color: '#94A3B8' }} /></InputAdornment>,
                                },
                            }}
                        />

                        <FormControl size="small">
                            <InputLabel>{t('history.actionType')}</InputLabel>
                            <Select
                                value={selectedAction}
                                onChange={(e) => onFilterChange(setSelectedAction)(e.target.value)}
                                label={t('history.actionType')}
                            >
                                <MenuItem value="">{t('history.all')}</MenuItem>
                                {ACTION_TYPES.map(action => (
                                    <MenuItem key={action} value={action}>
                                        <Stack direction="row" spacing={1} alignItems="center">
                                            {getActionIcon(action)}
                                            <span>{action}</span>
                                        </Stack>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <FormControl size="small" disabled={ruleOptions.length === 0}>
                            <InputLabel>{t('history.rule')}</InputLabel>
                            <Select
                                value={selectedRule}
                                onChange={(e) => onFilterChange(setSelectedRule)(e.target.value)}
                                label={t('history.rule')}
                            >
                                <MenuItem value="">{t('history.all')}</MenuItem>
                                {ruleOptions.map(rule => (
                                    <MenuItem key={rule} value={rule}>{rule}</MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        <TextField
                            label={t('history.since')}
                            type="date"
                            value={sinceDate}
                            onChange={(e) => onFilterChange(setSinceDate)(e.target.value)}
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                        />

                        <TextField
                            label={t('history.maxCount')}
                            type="number"
                            value={count}
                            onChange={(e) => setCount(Number(e.target.value))}
                            size="small"
                            slotProps={{ htmlInput: { min: 10, max: 1000, step: 10 } }}
                        />
                    </Box>

                    {activeFilterCount > 0 && (
                        <Stack direction="row" spacing={1} flexWrap="wrap" mt={1.5}>
                            {fileSearch && (
                                <Chip size="small" label={t('history.filterFile', { value: fileSearch })} onDelete={() => onFilterChange(setFileSearch)("")}
                                    sx={{ ...chipSx, bgcolor: 'rgba(37,99,235,0.08)', color: '#2563EB', border: '1px solid rgba(37,99,235,0.15)' }} />
                            )}
                            {selectedAction && (
                                <Chip size="small" label={t('history.filterAction', { value: selectedAction })} onDelete={() => onFilterChange(setSelectedAction)("")}
                                    sx={{ ...chipSx, ...ACTION_CHIP_COLORS[selectedAction.toLowerCase()], border: '1px solid rgba(0,0,0,0.06)' }} />
                            )}
                            {selectedRule && (
                                <Chip size="small" label={t('history.filterRule', { value: selectedRule })} onDelete={() => onFilterChange(setSelectedRule)("")}
                                    sx={{ ...chipSx, bgcolor: 'rgba(100,116,139,0.08)', color: '#64748B', border: '1px solid #E2E8F0' }} />
                            )}
                            {sinceDate && (
                                <Chip size="small" label={t('history.filterSince', { value: new Date(sinceDate).toLocaleDateString(locale) })} onDelete={() => onFilterChange(setSinceDate)("")}
                                    sx={{ ...chipSx, bgcolor: 'rgba(16,185,129,0.08)', color: '#059669', border: '1px solid rgba(16,185,129,0.15)' }} />
                            )}
                        </Stack>
                    )}
                </CardContent>
            </Card>

            {error && (
                <Alert severity="error" sx={{ flexShrink: 0 }}>
                    {t('history.loadError')}
                </Alert>
            )}

            {isLoading ? (
                <Card sx={{ flex: 1, border: '1px solid #E2E8F0', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <CircularProgress />
                        <Typography sx={{ mt: 2, color: '#64748B' }}>{t('history.loading')}</Typography>
                    </Box>
                </Card>
            ) : filtered.length > 0 ? (
                <Card sx={{ flex: 1, display: 'flex', flexDirection: 'column', border: '1px solid #E2E8F0', boxShadow: 'none', overflow: 'hidden', minHeight: 0 }}>
                    <TableContainer sx={{ flex: 1, overflow: 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    {[
                                        { label: t('history.colStatus'), width: 50 },
                                        { label: t('history.colAction'), width: 120 },
                                        { label: t('history.colFile'), width: 200 },
                                        { label: t('history.colFrom') },
                                        { label: t('history.colTo') },
                                        { label: t('history.colRule'), width: 150 },
                                        { label: t('history.colDate'), width: 150 },
                                    ].map(({ label, width }) => (
                                        <TableCell key={label} sx={{ ...HEADER_CELL_SX, ...(width ? { width } : {}) }}>
                                            {label}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item: HistoryRecordDto, index: number) => {
                                    const actionStyle = ACTION_CHIP_COLORS[(item.actionType ?? '').toLowerCase()] ?? { bg: 'rgba(100,116,139,0.1)', color: '#64748B' };
                                    return (
                                        <TableRow key={item.id || index} sx={{ '&:hover': { bgcolor: '#F8FAFC' } }}>
                                            <TableCell>
                                                <Tooltip title={item.message || t('history.success')}>
                                                    <span><CheckCircle sx={{ fontSize: 18, color: '#10B981' }} /></span>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip size="small" label={item.actionType} sx={{ ...chipSx, bgcolor: actionStyle.bg, color: actionStyle.color }} />
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={item.fileName || ''} arrow>
                                                    <Typography variant="body2" sx={{ fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 200, color: '#1E293B' }}>
                                                        {item.fileName || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={item.from || '—'} arrow>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, color: '#64748B' }}>
                                                        {item.from || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Tooltip title={item.to || '—'} arrow>
                                                    <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 300, color: '#64748B' }}>
                                                        {item.to || '—'}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell>
                                                <Chip label={item.ruleName} size="small" sx={{ ...chipSx, bgcolor: 'rgba(100,116,139,0.08)', color: '#64748B', border: '1px solid #E2E8F0' }} />
                                            </TableCell>
                                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#64748B' }}>
                                                {formatDate(item.timestamp) || '—'}
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filtered.length}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={(_, p) => setPage(p)}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage={t('common.rowsPerPage')}
                        labelDisplayedRows={({ from, to, count }) => t('common.displayedRows', { from, to, count })}
                        sx={{ flexShrink: 0, borderTop: '1px solid #E2E8F0', bgcolor: '#F8FAFC' }}
                    />
                </Card>
            ) : (
                <Card sx={{ flex: 1, border: '1px solid #E2E8F0', boxShadow: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Box sx={{ textAlign: 'center' }}>
                        <FolderSpecial sx={{ fontSize: 40, color: '#CBD5E1', mb: 1 }} />
                        <Typography variant="h6" sx={{ color: '#64748B' }}>
                            {activeFilterCount > 0 ? t('history.noMatch') : t('history.empty')}
                        </Typography>
                        <Typography variant="body2" sx={{ color: '#94A3B8', mt: 1 }}>
                            {activeFilterCount > 0 ? t('history.noMatchDesc') : t('history.emptyDesc')}
                        </Typography>
                        {activeFilterCount > 0 && (
                            <Button size="small" onClick={resetFilters} sx={{ mt: 2, color: '#2563EB' }}>
                                {t('history.clearFilters')}
                            </Button>
                        )}
                    </Box>
                </Card>
            )}
        </Box>
    );
}
